import React, { useCallback, useEffect, useRef, useState } from "react";
import { Popup } from "core/widgets/popup.widget";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import paths from "paths";
import { AuthActionTypes, useStateDispatcher } from "state";

const popupInterval = 900000;
const countdownInterval = popupInterval + 300000;

const useInactivityHook = () => {
  const rootElementRef = useRef<HTMLElement>();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const popupRef = useRef<HTMLDialogElement>(null);
  const [mounted, setMounted] = useState(false);
  const dispatcher = useStateDispatcher();
  const navigate = useNavigate();

  useEffect(() => {
    if (!rootElementRef.current) {
      const existingParent = document.getElementById("root");
      rootElementRef.current = existingParent!;
    }
    setMounted(true);

    const popUpRef = popupRef.current;

    return () => {
      setMounted(false);
      if (popUpRef) {
        popUpRef.remove();
      }
    };
  }, []);

  useEffect(() => {
    const handleWindowEvents = () => {
      clearInterval(timeoutRef.current);

      timeoutRef.current = setInterval(() => {
        if (popupRef.current) {
          popupRef.current.showModal();
        }
      }, /*15 minutes*/ popupInterval);
    };

    window.addEventListener("mousemove", handleWindowEvents);
    window.addEventListener("keydown", handleWindowEvents);
    window.addEventListener("click", handleWindowEvents);
    window.addEventListener("scroll", handleWindowEvents);

    return () => {
      window.removeEventListener("mousemove", handleWindowEvents);
      window.removeEventListener("keydown", handleWindowEvents);
      window.removeEventListener("click", handleWindowEvents);
      window.removeEventListener("scroll", handleWindowEvents);
      clearInterval(timeoutRef.current);
    };
  }, []);

  const closePopupCallback = useCallback(() => {
    if (popupRef.current) {
      popupRef.current.close();
    }
  }, [popupRef]);

  const logoutCallback = useCallback(() => {
    if (popupRef.current) {
      popupRef.current.close();
    }
    dispatcher({ type: AuthActionTypes.LOGOUT });
    navigate(`/${paths.open.LOGIN}`);
  }, [popupRef]);

  return mounted
    ? createPortal(
        <Popup
          title="are you still there?"
          ref={popupRef}
          description="It seems you've been inactive for the last 15 minutes. Your session will terminate in 5:00 minutes for security reasons"
          intervalTime={countdownInterval}
          onAccept={closePopupCallback}
          onIntervalCallback={logoutCallback}
          onReject={logoutCallback}
          onRejectMessage="Log out"
          onAcceptMessage="Keep me logged in"
        />,
        rootElementRef.current!,
      )
    : null;
};

export default useInactivityHook;
